import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import { isValidEmail } from '../utils/validators';
import { CONTACT_ENDPOINTS } from '../utils/constants';
import SocialLinks from '../components/contact/SocialLinks';

import '../styles/global.css';
import '../styles/pages/contact.css';

// Constants
const ALERT_DURATION = 5000;

const ContactOut = () => {
    // State for form fields
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // State for alert
    const [alert, setAlert] = useState({
        isOpen: false,
        title: '',
        message: '',
        footer: '',
        type: 'success'
    });

    // Alert handling functions
    const closeAlert = () => {
        setAlert({ ...alert, isOpen: false });
    };

    const showAlert = (title, message, type = 'success', footer = '') => {
        setAlert({
            isOpen: true,
            title,
            message,
            footer,
            type
        });

        setTimeout(() => {
            closeAlert();
        }, ALERT_DURATION);
    };

    // Form validation
    const validateForm = () => {
        if (!fullName || !email || !message) {
            showAlert(
                'Missing Information',
                'Please fill in all fields',
                'fail',
                'All fields are required to send a message'
            );
            return false;
        }

        if (!isValidEmail(email)) {
            showAlert(
                'Invalid Email',
                'Please enter a valid email address',
                'fail',
                'The email format is incorrect'
            );
            return false;
        }

        return true;
    };

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(CONTACT_ENDPOINTS.SUBMIT_FORM, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: fullName,
                    email,
                    message
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to send message');
            }

            // Clear form fields on success
            setFullName('');
            setEmail('');
            setMessage('');

            showAlert(
                'Message Sent',
                'Your message has been sent successfully',
                'success',
                'We will get back to you soon'
            );
        } catch (error) {
            console.error('Error sending message:', error);
            showAlert(
                'Error',
                error.message || 'Failed to send message',
                'fail',
                'Please try again or contact support if the issue persists'
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Navbar />

            <div className="main-container">
                

                <div className="row">
                    <div className="col-left">
                        {/* Contact Form */}
                        <div className="contact-card">
                            <div className="card-header">
                                <h2>Send us a message</h2>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label htmlFor="fullName" className="form-label">Full Name (Alias)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="fullName"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder="Enter your name"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email" className="form-label">Email Address</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="message" className="form-label">Message</label>
                                        <textarea
                                            className="form-control"
                                            id="message"
                                            rows="5"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Write your message here..."
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                                Sending...
                                            </>
                                        ) : 'Send Message'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="col-right">
                        {/* Contact Information */}
                        <div className="info-card">
                            <div className="contact-info">
                                <h2>Contact Information</h2>
                                <a
                                    href="mailto:phenixloc@finnovant.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="email-link"
                                >
                                    phenixloc@finnovant.com
                                </a>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="social-card">
                            <h5>Follow Us</h5>
                            <SocialLinks />
                        </div>
                    </div>
                </div>
            </div>

            <Alert
                isOpen={alert.isOpen}
                onClose={closeAlert}
                title={alert.title}
                message={alert.message}
                footer={alert.footer}
                type={alert.type}
            />
        </>
    );
};

export default ContactOut;